import { useState, useMemo } from "react";

import * as Cmp from "@impero-com/common/utils/comparison";
import * as DV from "@impero-com/common/utils/dataView";

import {
    ControlWithoutFieldsGraphItemDto,
    CreateActions,
} from "api/generated/model";
import { useControlProgramsQuery } from "org/control/queries/useControlProgramsQuery";
import { useControlsQuery } from "org/control/queries/useControlsQuery";
import { useTagsSuspenseQuery } from "org/tags/queries/useTagsQuery";
import { useOrgUserProfileCookie } from "utils/profileCookie";
import { useOrgDisplayInfo } from "utils/queries/useOrgDisplayInfoSuspenseQuery";

const EXPORT_CONFIG = {
    FILENAME: "control-assignments",
    FILE_FORMAT: "xlsx",
    SHEET_NAME: "Bulk Controls Assignment",
};

interface ExportRow {
    control: {
        resource: ControlWithoutFieldsGraphItemDto;
        actions: CreateActions;
    };
    controlProgram: {
        resource: {
            id: number;
            name: string;
            description: string;
        };
        actions: CreateActions;
    };
    assigneeTags: {
        tags: Array<{
            id: string;
            name: string;
            tagCategoryId: string;
        }>;
        tagCategories: Array<{
            id: string;
            name: string;
        }>;
    };
}

interface ExportColumn {
    label: string;
    getValue: (row: ExportRow) => string;
}

interface UseControlProgramExportProps {
    onExport: () => void;
}

function getExportColumns(categoryMap: Map<string, string>): ExportColumn[] {
    const baseColumns: ExportColumn[] = [
        {
            label: "ControlProgramName",
            getValue: (row: ExportRow) => row.controlProgram.resource.name,
        },
        {
            label: "ControlProgram_ID",
            getValue: (row: ExportRow) =>
                row.controlProgram.resource.id.toString(),
        },
        {
            label: "ControlName",
            getValue: (row: ExportRow) => row.control.resource.title,
        },
        {
            label: "Control_ID",
            getValue: (row: ExportRow) => row.control.resource.id,
        },
        {
            label: "Responsible",
            getValue: (_row: ExportRow) => "",
        },
        {
            label: "Responsible_ID",
            getValue: (_row: ExportRow) => "",
        },
        {
            label: "Responsible_Group_ID",
            getValue: (_row: ExportRow) => "",
        },
        {
            label: "Default_Claimant_ID",
            getValue: (_row: ExportRow) => "",
        },
        {
            label: "GroupAssignment_Type",
            getValue: (_row: ExportRow) => "",
        },
        {
            label: "Reviewer",
            getValue: (_row: ExportRow) => "",
        },
        {
            label: "Reviewer_ID",
            getValue: (_row: ExportRow) => "",
        },
        {
            label: "Reviewer_Group_ID",
            getValue: (_row: ExportRow) => "",
        },
        {
            label: "SecondReviewer",
            getValue: (_row: ExportRow) => "",
        },
        {
            label: "SecondReviewer_ID",
            getValue: (_row: ExportRow) => "",
        },
        {
            label: "SecondReviewer_Group_ID",
            getValue: (_row: ExportRow) => "",
        },
        {
            label: "Entity",
            getValue: (_row: ExportRow) => "",
        },
        {
            label: "Entity_ID",
            getValue: (_row: ExportRow) => "",
        },
    ];

    const tagColumns = Array.from(categoryMap.entries()).map(
        ([_categoryId, categoryName]) => ({
            label: "AssigneeTags: " + categoryName,
            getValue: (_row: ExportRow) => "",
        })
    );

    return [...baseColumns, ...tagColumns];
}

export const useControlProgramExport = ({
    onExport,
}: UseControlProgramExportProps) => {
    const [selectedPrograms, setSelectedPrograms] = useState<number[]>([]);
    const { data: controlProgramOverviews } = useControlProgramsQuery();
    const { data: controls } = useControlsQuery();
    const { data: tagsData } = useTagsSuspenseQuery();
    const orgCookie = useOrgDisplayInfo();
    const orgProfileCookie = useOrgUserProfileCookie();

    const controlPrograms = controlProgramOverviews?.content;

    const programOptions = useMemo(
        () =>
            controlPrograms?.map((cp) => ({
                label: cp.resource.controlProgram.name,
                value: cp.resource.controlProgram.id,
            })) ?? [],
        [controlPrograms]
    );

    const handleSelectAll = (selected: boolean) => {
        if (selected) {
            setSelectedPrograms(programOptions.map((option) => option.value));
        } else {
            setSelectedPrograms([]);
        }
    };

    const handleExport = () => {
        if (
            !controls ||
            !orgProfileCookie ||
            !orgCookie ||
            selectedPrograms.length === 0 ||
            !controlPrograms ||
            !tagsData
        ) {
            return;
        }

        const filteredControls = controls.content.filter((control) =>
            selectedPrograms.includes(control.resource.controlProgramId)
        );

        const categoryMap = new Map(
            tagsData.tagCategories
                .filter((category) => category.active)
                .sort((a, b) => Cmp.string()(a.name, b.name))
                .map((category) => [category.id, category.name])
        );

        const columns = getExportColumns(categoryMap);

        const defaultControlActions: CreateActions = {
            create: true,
        };

        const defaultControlProgramActions: CreateActions = {
            create: true,
            ...controlPrograms[0].actions,
        };

        const dataView: DV.DataView<ExportRow> = {
            dataSource: new DV.ArrayDataSource(
                filteredControls.map((control) => ({
                    control: {
                        resource: control.resource,
                        actions: defaultControlActions,
                    },
                    controlProgram: {
                        resource: {
                            id: control.resource.controlProgramId,
                            name:
                                controlPrograms.find(
                                    (cp) =>
                                        cp.resource.controlProgram.id ===
                                        control.resource.controlProgramId
                                )?.resource.controlProgram.name ?? "",
                            description: "",
                        },
                        actions: defaultControlProgramActions,
                    },
                    assigneeTags: {
                        tags: tagsData.tags.map((tag) => ({
                            id: tag.id,
                            name: tag.name,
                            tagCategoryId: tag.tagCategoryId,
                        })),
                        tagCategories: tagsData.tagCategories.map((cat) => ({
                            id: cat.id,
                            name: cat.name,
                        })),
                    },
                })),
                (row) => row.control.resource.id
            ),
            extractorGroups: [
                {
                    id: "export",
                    label: "Export",
                    extractors: columns.map((col) => ({
                        id: col.label,
                        label: col.label,
                        getValue: col.getValue,
                        compare: Cmp.map(col.getValue, Cmp.string()),
                        asString: (row: ExportRow) =>
                            col.getValue(row)?.toString() ?? "",
                    })),
                },
            ],
            sorters: [],
            defaultCompareItems: Cmp.primitive,
        };

        DV.exportToExcel(
            dataView,
            () => true,
            columns,
            EXPORT_CONFIG.FILENAME,
            EXPORT_CONFIG.FILE_FORMAT,
            EXPORT_CONFIG.SHEET_NAME,
            {
                date: new Date(),
                organization: {
                    name: orgCookie.name,
                },
                user: {
                    firstName: orgProfileCookie.firstName,
                    lastName: orgProfileCookie.lastName,
                    email: orgProfileCookie.email,
                },
            }
        );
        onExport();
    };

    return {
        selectedPrograms,
        setSelectedPrograms,
        programOptions,
        handleSelectAll,
        handleExport,
    };
};
